import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { API_ENDPOINTS, apiClient } from '../config/api';
import { auth } from '../firebase/config';
import { getActiveRole } from '../utils/getActiveRole';
import SharePropertyModal from '../components/property/SharePropertyModal';
import NotesPanel from '../components/notes/NotesPanel';
import './PropertyDetailPage.css';

const PropertyDetailPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { transactionId, propertyId, id } = useParams();
  // Support both routes: /property/:id (standalone) and /transaction/:transactionId/property/:propertyId
  const mlsId = propertyId || id;
  const isStandalone = !transactionId;
  // Property data passed from TransactionProperties "View Details" — avoids calling the MLS API
  const passedPropertyData = location.state?.propertyData || null;
  const [transaction, setTransaction] = useState(null);
  const [property, setProperty] = useState(null);
  const [isLoading, setIsLoading] = useState(!isStandalone);
  const [isPropertyLoading, setIsPropertyLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('properties');
  const [isInterested, setIsInterested] = useState(false);
  // The interested-property record's own id (UUID) — distinct from mlsId.
  // Required by DELETE /interested-properties/{id}; the check/{mlsId} endpoint
  // only returns a boolean and cannot supply it (matches mobile app behavior).
  const [interestedPropertyRecordId, setInterestedPropertyRecordId] = useState(null);
  const [interestedPropertyRpaCreated, setInterestedPropertyRpaCreated] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [documentsCount, setDocumentsCount] = useState(0);
  const [participantsCount, setParticipantsCount] = useState(0);
  const [propertiesCount, setPropertiesCount] = useState(0);
  const [interestedPropertiesList, setInterestedPropertiesList] = useState([]);
  const [userRole, setUserRole] = useState('');
  const [showShareModal, setShowShareModal] = useState(false);
  const [showInterestedModal, setShowInterestedModal] = useState(false);
  const [showCancelInterestedConfirm, setShowCancelInterestedConfirm] = useState(false);
  const [buyerTransactions, setBuyerTransactions] = useState([]);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [interestedLoading, setInterestedLoading] = useState(false);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [isFavorite, setIsFavorite] = useState(false);
  const [isTogglingFavorite, setIsTogglingFavorite] = useState(false);
  const touchStartX = useRef(0);
  const thumbnailStripRef = useRef(null);

  // Detect user role from localStorage — always use the active/switched role
  useEffect(() => {
    try {
      const storedUserData = localStorage.getItem('user');
      const parsedData = storedUserData ? JSON.parse(storedUserData) : null;
      setUserRole(getActiveRole(parsedData));
    } catch (error) {
      console.error('Error parsing user data:', error);
      setUserRole('');
    }
  }, []);

  const isAgent = userRole === 'AGENT' || userRole === 'TRANSACTION_COORDINATOR';
  // Listing agent's email is sensitive contact info — only shown to users
  // logged in with the AGENT role (not TC, buyers, sellers, etc).
  const isAgentRole = userRole === 'AGENT';

  // Function to get Firebase token
  const getFirebaseToken = useCallback(() => {
    return new Promise((resolve, reject) => {
      // If user is already available, get token directly
      if (auth.currentUser) {
        auth.currentUser.getIdToken(true).then(resolve).catch(reject);
        return;
      }
      // Otherwise wait for auth state to be ready (with timeout)
      const timeoutId = setTimeout(() => {
        unsubscribe();
        reject(new Error('Firebase auth timeout'));
      }, 10000);
      const unsubscribe = auth.onAuthStateChanged(async (user) => {
        clearTimeout(timeoutId);
        unsubscribe();
        if (user) {
          try {
            const token = await user.getIdToken(true);
            resolve(token);
          } catch (error) {
            reject(error);
          }
        } else {
          reject(new Error('No authenticated user'));
        }
      });
    });
  }, []);

  // Fetch transaction data
  useEffect(() => {
    const fetchTransactionData = async () => {
      if (!transactionId) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        const firebaseToken = await getFirebaseToken();

        // DocuSign is COLORADO-ONLY — skip the DocuSign documents fetch for non-CO agents (they
        // use Lone Wolf, no DocuSign documents). The transaction's own state isn't known yet
        // (fetched in this same Promise.all), so gate on the agent's licenseState.
        const _propAgentState = (() => {
          try {
            const u = JSON.parse(localStorage.getItem('user') || '{}');
            return (u.licenseState || u.stateCode || u.state || '').toUpperCase();
          } catch { return ''; }
        })();
        const _propIsColoradoAgent = _propAgentState === 'CO' || _propAgentState === '';

        const [transactionResponse, participantsResponse, documentsResponse, propertiesResponse] = await Promise.all([
          apiClient.get(API_ENDPOINTS.transactionById(transactionId), {
            headers: { 'Authorization': `Bearer ${firebaseToken}`, 'Content-Type': 'application/json' }
          }),
          apiClient.get(API_ENDPOINTS.transactionParticipants(transactionId), {
            headers: { 'Authorization': `Bearer ${firebaseToken}`, 'Content-Type': 'application/json' }
          }).catch(() => ({ data: [] })),
          (_propIsColoradoAgent
            ? apiClient.get(API_ENDPOINTS.transactionDocusignDocuments(transactionId), {
                headers: { 'Authorization': `Bearer ${firebaseToken}`, 'Content-Type': 'application/json' }
              })
            : Promise.resolve({ data: [] })
          ).catch(() => ({ data: [] })),
          apiClient.get(API_ENDPOINTS.interestedProperties(transactionId), {
            headers: { 'Authorization': `Bearer ${firebaseToken}`, 'Content-Type': 'application/json' }
          }).catch(() => ({ data: [] }))
        ]);

        setTransaction(transactionResponse.data);

        // Count participants
        const pData = participantsResponse.data;
        const pList = Array.isArray(pData) ? pData : (pData?.data || pData?.participants || pData?.content || []);
        setParticipantsCount(pList.length);

        // Count documents
        const dData = documentsResponse.data;
        const dList = Array.isArray(dData) ? dData : (dData?.data || dData?.documents || dData?.content || []);
        setDocumentsCount(dList.length);

        // Count properties
        const prData = propertiesResponse.data;
        const prList = Array.isArray(prData) ? prData : (prData?.data || prData?.properties || prData?.results || prData?.content || []);
        setPropertiesCount(prList.length);
        setInterestedPropertiesList(prList);
      } catch (error) {
        console.error('Error fetching transaction data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTransactionData();
  }, [transactionId, getFirebaseToken]);

  // Fetch property data
  useEffect(() => {
    const fetchPropertyData = async () => {
      if (!mlsId) {
        setIsPropertyLoading(false);
        return;
      }

      // Use data passed via navigation state (from TransactionProperties "View Details")
      if (passedPropertyData) {
        setProperty(passedPropertyData);
        setIsPropertyLoading(false);
        return;
      }

      setIsPropertyLoading(true);
      try {
        const firebaseToken = await getFirebaseToken();

        const response = await apiClient.get(
          API_ENDPOINTS.mlsProperty(mlsId),
          {
            headers: {
              'Authorization': `Bearer ${firebaseToken}`,
              'Content-Type': 'application/json'
            }
          }
        );

        console.log('Property data fetched:', response.data);

        // Handle different response formats
        const data = response.data;
        if (Array.isArray(data) && data.length > 0) {
          setProperty(data[0]);
        } else if (data && typeof data === 'object' && !Array.isArray(data)) {
          setProperty(data);
        }
      } catch (error) {
        console.error('Error fetching property:', error);
      } finally {
        setIsPropertyLoading(false);
      }
    };

    fetchPropertyData();
  }, [mlsId, getFirebaseToken, passedPropertyData]);

  // Check if buyer already interested in this property
  useEffect(() => {
    const checkInterestStatus = async () => {
      if (!transactionId || !mlsId || isAgent) return;
      try {
        const firebaseToken = await getFirebaseToken();
        const response = await apiClient.get(
          API_ENDPOINTS.checkInterestedProperty(transactionId, String(mlsId)),
          {
            headers: {
              'Authorization': `Bearer ${firebaseToken}`,
              'Content-Type': 'application/json'
            }
          }
        );
        if (response.data?.exists) {
          setIsInterested(true);
        }
      } catch (error) {
        console.error('Error checking interest status:', error);
      }
    };

    checkInterestStatus();
  }, [transactionId, mlsId, isAgent, getFirebaseToken]);

  // Hydrate this property's favorite status — reached directly (deep link,
  // "View Details" from search) so it can't rely on a list-level fetch
  // (mirrors mobile PropertyDetailScreen's fetchFavoriteIds-on-mount).
  useEffect(() => {
    const loadFavoriteStatus = async () => {
      if (!mlsId) return;
      try {
        const firebaseToken = await getFirebaseToken();
        const response = await apiClient.get(API_ENDPOINTS.favoritesIds, {
          headers: { 'Authorization': `Bearer ${firebaseToken}` }
        });
        const ids = response.data?.propertyIds ?? [];
        setIsFavorite(ids.map(String).includes(String(mlsId)));
      } catch (error) {
        console.error('Error checking favorite status:', error);
      }
    };

    loadFavoriteStatus();
  }, [mlsId, getFirebaseToken]);

  // Toggle favorite — optimistic flip with rollback on server failure
  // (mirrors mobile toggleFavoriteThunk).
  const handleToggleFavorite = async () => {
    if (!mlsId || isTogglingFavorite) return;
    const wasFavorite = isFavorite;
    setIsFavorite(!wasFavorite);
    setIsTogglingFavorite(true);
    try {
      const firebaseToken = await getFirebaseToken();
      if (wasFavorite) {
        await apiClient.delete(API_ENDPOINTS.favoriteProperty(mlsId), {
          headers: { 'Authorization': `Bearer ${firebaseToken}` }
        }).catch(err => {
          if (err?.response?.status !== 404) throw err;
        });
      } else {
        await apiClient.post(API_ENDPOINTS.favoriteProperty(mlsId), {}, {
          headers: { 'Authorization': `Bearer ${firebaseToken}` }
        });
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
      setIsFavorite(wasFavorite);
      alert('Could not update favorite. Please try again.');
    } finally {
      setIsTogglingFavorite(false);
    }
  };

  // Resolve the interested-property record's own id (needed for cancel) by
  // matching mlsId against the transaction's interested-properties list —
  // the check/{mlsId} endpoint above only returns a boolean, never the id.
  useEffect(() => {
    if (!mlsId || interestedPropertiesList.length === 0) {
      setInterestedPropertyRecordId(null);
      setInterestedPropertyRpaCreated(false);
      return;
    }
    const match = interestedPropertiesList.find(
      (item) => String(item?.mlsId) === String(mlsId)
    );
    setInterestedPropertyRecordId(match?.id || match?.interestedPropertyId || null);
    setInterestedPropertyRpaCreated(Boolean(match?.rpaCreated));
  }, [mlsId, interestedPropertiesList]);

  // Helper: get full address from transaction
  const getFullAddress = () => {
    if (!transaction) return '';
    const parts = [
      transaction.propertyAddress,
      transaction.propertyCity,
      transaction.propertyState,
      transaction.propertyZip
    ].filter(Boolean);
    return parts.join(', ') || 'No address';
  };

  // Helper: format status
  const formatStatus = (status) => {
    const statusText = {
      'PENDING': 'Prospect',
      'ACTIVE': 'Active',
      'UNDER_CONTRACT': 'Under Contract',
      'CLOSING': 'Closing',
      'CANCELLED': 'Fell Through',
      'CLOSED': 'Closed'
    };
    return statusText[status] || status;
  };

  // Property field helpers
  const getPropertyAddress = (prop) => {
    if (!prop) return 'Unknown Address';
    return prop.address || prop.propertyAddress || prop.fullAddress || prop.name || 'Unknown Address';
  };

  const getPropertyCity = (prop) => {
    if (!prop) return '';
    const city = prop.city || prop.propertyCity || '';
    const state = prop.state || prop.propertyState || '';
    const zip = prop.zipCode || prop.zip || prop.postalCode || prop.propertyZip || '';
    const parts = [city, state].filter(Boolean).join(', ');
    return zip ? `${parts} ${zip}` : parts;
  };

  const getPropertyFullAddress = (prop) => {
    if (!prop) return 'Unknown Address';
    const street = getPropertyAddress(prop);
    const cityState = getPropertyCity(prop);
    return cityState ? `${street}, ${cityState}` : street;
  };

  const getPropertyLat = (prop) => {
    if (!prop) return null;
    const lat = prop.latitude ?? prop.lat ?? prop.location?.latitude ?? prop.location?.lat;
    return (lat === undefined || lat === null || lat === '') ? null : Number(lat);
  };

  const getPropertyLng = (prop) => {
    if (!prop) return null;
    const lng = prop.longitude ?? prop.lng ?? prop.location?.longitude ?? prop.location?.lng;
    return (lng === undefined || lng === null || lng === '') ? null : Number(lng);
  };

  const getPropertyPrice = (prop) => {
    if (!prop) return '$0';
    const price = prop.listPrice || prop.price || prop.askingPrice || prop.salePrice || 0;
    return `$${Number(price).toLocaleString()}`;
  };

  const getPropertyMls = (prop) => {
    if (!prop) return '';
    return prop.mlsId || prop.mlsNumber || prop.listingId || prop.id || '';
  };

  const getPropertyBeds = (prop) => {
    if (!prop) return '0';
    return prop.bedrooms || prop.beds || prop.bedroomCount || '0';
  };

  const getPropertyBaths = (prop) => {
    if (!prop) return '0';
    return prop.bathrooms || prop.baths || prop.bathroomCount || '0';
  };

  const getPropertySqft = (prop) => {
    if (!prop) return '0';
    const sqft = prop.squareFeet || prop.sqft || prop.livingArea || prop.area || 0;
    return Number(sqft).toLocaleString();
  };

  const getPropertyYearBuilt = (prop) => {
    if (!prop) return 'N/A';
    return prop.yearBuilt || prop.year_built || prop.builtYear || 'N/A';
  };

  // Mirrors mobile PropertyDetailScreen.tsx spec-grid Days box — backend sends a
  // precomputed value, no client-side date math needed.
  const getPropertyDaysOnMarket = (prop) => {
    if (!prop) return '—';
    const dom = prop.daysOnMarket;
    return (dom === undefined || dom === null) ? '—' : dom;
  };

  // Numeric price for the monthly-payment estimate — getPropertyPrice() above
  // returns a formatted "$450,000" string, not a number suitable for math.
  const getPropertyPriceNumber = (prop) => {
    if (!prop) return 0;
    return Number(prop.listPrice || prop.price || prop.askingPrice || prop.salePrice || 0) || 0;
  };

  // Mirrors mobile PropertyDetailScreen.tsx calculateMonthlyPayment() verbatim —
  // same assumptions (20% down, 7% interest, 30yr term, 1.2%/yr tax, flat $150/mo
  // insurance, hoaFee/12 if present). Cosmetic estimate only, not a real quote.
  const getEstMonthlyPayment = (prop) => {
    const price = getPropertyPriceNumber(prop);
    if (!price) return null;

    const downPayment = price * 0.2;
    const loanAmount = price - downPayment;
    const monthlyInterestRate = 0.07 / 12;
    const numberOfPayments = 30 * 12;

    const monthlyMortgage =
      (loanAmount * (monthlyInterestRate * Math.pow(1 + monthlyInterestRate, numberOfPayments))) /
      (Math.pow(1 + monthlyInterestRate, numberOfPayments) - 1);

    const propertyTax = (price * 0.012) / 12;
    const insurance = 150;
    const hoa = prop?.hoaFee ? prop.hoaFee / 12 : 0;

    return Math.round(monthlyMortgage + propertyTax + insurance + hoa);
  };

  // Mirrors mobile PropertyDetailScreen.tsx Market Insight card — canned copy keyed
  // off daysOnMarket thresholds, not real market data.
  const getMarketInsightText = (prop) => {
    const dom = prop?.daysOnMarket ?? 0;
    if (dom <= 7) return 'Hot property! Listed recently in a competitive market';
    if (dom <= 30) return 'Property is priced competitively for this area';
    return 'Property has been on market for a while - potential for negotiation';
  };

  // Flat fields (schoolDistrict etc.) come from the same /mls/properties/{mlsId}
  // endpoint mobile uses; also check the nested property.schools.* shape used
  // elsewhere in this file (handleConfirmInterested) for safety.
  const getPropertySchools = (prop) => {
    if (!prop) return null;
    const elementary = prop.schools?.elementary || prop.elementarySchool || '';
    const middle = prop.schools?.middle || prop.middleSchool || '';
    const high = prop.schools?.high || prop.highSchool || '';
    const district = prop.schools?.district || prop.schoolDistrict || '';
    if (!elementary && !middle && !high && !district) return null;
    return { elementary, middle, high, district };
  };

  const getPropertyDescription = (prop) => {
    if (!prop) return '';
    return prop.description || prop.remarks || prop.publicRemarks || prop.privateRemarks || '';
  };

  const getPropertyFeatures = (prop) => {
    if (!prop) return [];
    if (Array.isArray(prop.features)) return prop.features;
    if (Array.isArray(prop.amenities)) return prop.amenities;
    // Build features from available fields
    const features = [];
    if (prop.cooling || prop.airConditioning) features.push('Central Air Conditioning');
    if (prop.heating) features.push('Heating System');
    if (prop.flooring) features.push(prop.flooring);
    if (prop.garage || prop.garageSpaces) features.push(`${prop.garageSpaces || ''} Car Garage`.trim());
    if (prop.pool) features.push('Pool');
    if (prop.fireplace) features.push('Fireplace');
    if (prop.lotSize) features.push(`Lot: ${prop.lotSize} sqft`);
    if (prop.stories) features.push(`${prop.stories} Stories`);
    return features;
  };

  const getPropertyDetails = (prop) => {
    if (!prop) return 'Details N/A';
    const beds = prop.bedrooms || prop.beds || prop.bedroomCount || '';
    const baths = prop.bathrooms || prop.baths || prop.bathroomCount || '';
    const sqft = prop.squareFeet || prop.sqft || prop.livingArea || prop.area || '';
    const parts = [];
    if (beds) parts.push(`${beds} beds`);
    if (baths) parts.push(`${baths} baths`);
    if (sqft) parts.push(`${Number(sqft).toLocaleString()} sqft`);
    return parts.join(' \u2022 ') || 'Details N/A';
  };

  const getPropertyPhoto = (prop) => {
    if (!prop) return '';
    if (prop.photoUrl) return prop.photoUrl;
    if (prop.mainPhoto) return prop.mainPhoto;
    if (prop.imageUrl) return prop.imageUrl;
    if (prop.thumbnailUrl) return prop.thumbnailUrl;
    if (prop.primaryPhoto) return prop.primaryPhoto;
    if (Array.isArray(prop.photos) && prop.photos.length > 0) {
      const first = prop.photos[0];
      return typeof first === 'string' ? first : (first?.url || first?.href || '');
    }
    if (Array.isArray(prop.images) && prop.images.length > 0) {
      const first = prop.images[0];
      return typeof first === 'string' ? first : (first?.url || first?.href || '');
    }
    if (Array.isArray(prop.media) && prop.media.length > 0) {
      const first = prop.media[0];
      return typeof first === 'string' ? first : (first?.url || first?.href || '');
    }
    return '';
  };

  const getPropertyPhotos = (prop) => {
    if (!prop) return [];
    const raw = prop.photos || prop.images || prop.media || [];
    if (Array.isArray(raw) && raw.length > 0) {
      return raw.map(p => typeof p === 'string' ? p : (p?.url || p?.href || p?.Uri || '')).filter(Boolean);
    }
    const single = prop.primaryPhoto || prop.photoUrl || prop.mainPhoto || prop.imageUrl || prop.thumbnailUrl;
    return single ? [single] : [];
  };

  // Agent detail helpers — mirror mobile property.service.ts mapping:
  //   Detailed MLSProperty:  mls.agent.firstName+lastName, mls.agent.contact.email/cell, mls.office.name
  //   Simplified endpoint:   data.listingAgentName, data.listingOfficeName
  const getAgentName = (prop) => {
    if (!prop) return '';
    // Nested agent object (detailed MLS response)
    if (prop.agent?.firstName || prop.agent?.lastName) {
      return `${prop.agent.firstName || ''} ${prop.agent.lastName || ''}`.trim();
    }
    // Simplified response fields (matches mobile data.listingAgentName)
    return prop.listingAgentName
      || prop.listingAgent?.name
      || prop.listAgentFullName
      || prop.agentName
      || prop.listAgent
      || '';
  };

  const getAgentOffice = (prop) => {
    if (!prop) return '';
    return prop.listingAgent?.office
      || prop.listingOfficeName
      || prop.office?.name
      || prop.office?.servingName
      || '';
  };

  const getAgentEmail = (prop) => {
    if (!prop) return '';
    return prop.listingAgent?.email
      || prop.listingAgentEmail
      || prop.agent?.contact?.email
      || '';
  };

  const getAgentPhone = (prop) => {
    if (!prop) return '';
    return prop.listingAgent?.phone
      || prop.listingAgentPhone
      || prop.agent?.contact?.cell
      || prop.agent?.contact?.office
      || '';
  };

  const getAgentInitials = (name) => {
    if (!name) return 'LA';
    return name.split(' ').filter(Boolean).map(n => n[0]).join('').toUpperCase().slice(0, 2) || 'LA';
  };

  // Handle "I'm Interested" click - fetch buyer's transactions first
  const handleInterested = async () => {
    setInterestedLoading(true);
    try {
      const firebaseToken = await getFirebaseToken();

      // Step 1: Fetch buyer's PURCHASE transactions (PENDING or ACTIVE)
      const response = await apiClient.get(
        `${API_ENDPOINTS.transactions}?status=PENDING&status=ACTIVE&type=PURCHASE`,
        {
          headers: {
            'Authorization': `Bearer ${firebaseToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      const data = response.data;
      const txnList = Array.isArray(data) ? data : (data?.data || data?.content || data?.transactions || []);

      if (txnList.length > 0) {
        setBuyerTransactions(txnList);
        setSelectedTransaction(txnList[0]); // Default to first transaction
        setShowInterestedModal(true);
      } else {
        alert('No active purchase transactions found. Please create a transaction first.');
      }
    } catch (error) {
      console.error('Error fetching transactions:', error);
      alert('Unable to fetch transactions. Please try again.');
    } finally {
      setInterestedLoading(false);
    }
  };

  // Confirm adding interested property
  const handleConfirmInterested = async () => {
    if (!selectedTransaction) return;
    setInterestedLoading(true);
    try {
      const firebaseToken = await getFirebaseToken();
      const txnId = selectedTransaction.id || selectedTransaction.transactionId;

      // Step 2: Check if property already exists in this transaction
      const checkResponse = await apiClient.get(
        API_ENDPOINTS.checkInterestedProperty(txnId, String(mlsId)),
        {
          headers: {
            'Authorization': `Bearer ${firebaseToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (checkResponse.data?.exists) {
        setShowInterestedModal(false);
        alert('This property is already in your interested properties for this transaction.');
        return;
      }

      // Step 3: Add interested property
      const addResponse = await apiClient.post(
        API_ENDPOINTS.interestedProperties(txnId),
        {
          mlsId: String(mlsId),
          propertyAddress: getPropertyAddress(property),
          propertyCity: property?.city || property?.propertyCity || '',
          propertyState: property?.state || property?.propertyState || '',
          propertyZip: property?.zipCode || property?.zip || property?.postalCode || property?.propertyZip || '',
          listPrice: property?.listPrice || property?.price || 0,
          bedrooms: property?.bedrooms || property?.beds || property?.bedroomCount || undefined,
          bathrooms: property?.bathrooms || property?.baths || property?.bathroomCount || undefined,
          squareFeet: property?.squareFeet || property?.sqft || property?.livingArea || property?.area || undefined,
          primaryPhoto: getPropertyPhoto(property) || undefined,
          photos: Array.isArray(property?.photos) && property.photos.length > 0 ? property.photos : undefined,
          propertyType: property?.propertyType || undefined,
          yearBuilt: property?.yearBuilt || undefined,
          lotSize: property?.lotSize || undefined,
          description: property?.description || property?.remarks || property?.publicRemarks || undefined,
          daysOnMarket: property?.daysOnMarket || undefined,
          hoaFee: property?.hoaFee || undefined,
          taxAnnualAmount: property?.taxAnnualAmount || property?.taxAmount || undefined,
          garageSpaces: property?.garageSpaces || undefined,
          county: property?.county || undefined,
          country: property?.country || undefined,
          stories: property?.stories || undefined,
          lotSizeAcres: property?.lotSizeAcres || undefined,
          taxId: property?.taxId || undefined,
          schoolDistrict: property?.schools?.district || property?.schoolDistrict || undefined,
          listingAgentName: property?.listAgentFullName || property?.listingAgentName || property?.agentName || undefined,
          listingAgentEmail: property?.listingAgentEmail || undefined,
          listingAgentPhone: property?.listingAgentPhone || undefined,
          listingOfficeName: property?.listingOfficeName || undefined,
          sourceUrl: property?.sourceUrl || property?.zillowUrl || property?.listingUrl || property?.externalUrl || undefined,
        },
        {
          headers: {
            'Authorization': `Bearer ${firebaseToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      setShowInterestedModal(false);
      setIsInterested(true);
      // Only trust the returned record id for the transaction currently being
      // viewed — the buyer may have added the property to a different one.
      if (txnId === transactionId) {
        setInterestedPropertyRecordId(addResponse?.data?.id || addResponse?.data?.interestedPropertyId || null);
        setInterestedPropertyRpaCreated(false);
      }
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Error adding interested property:', error);
      setShowInterestedModal(false);
      alert('Failed to add property. Please try again.');
    } finally {
      setInterestedLoading(false);
    }
  };

  // Cancel interested property — clicking the button opens the standard
  // Yes/No confirmation popup (same style as "Mark as Interested" above)
  // instead of a native window.confirm.
  const handleCancelInterestedClick = () => {
    if (interestedPropertyRpaCreated) {
      alert('This property has an RPA created and cannot be removed.');
      return;
    }
    if (!interestedPropertyRecordId) {
      alert('Unable to cancel interest right now. Please refresh the page and try again.');
      return;
    }
    setShowCancelInterestedConfirm(true);
  };

  const handleConfirmCancelInterested = async () => {
    setInterestedLoading(true);
    try {
      const firebaseToken = await getFirebaseToken();

      await apiClient.delete(
        API_ENDPOINTS.deleteInterestedProperty(transactionId, interestedPropertyRecordId),
        {
          headers: {
            'Authorization': `Bearer ${firebaseToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      setIsInterested(false);
      setInterestedPropertyRecordId(null);
      setShowCancelInterestedConfirm(false);
    } catch (error) {
      console.error('Error cancelling interest:', error);
      setShowCancelInterestedConfirm(false);
      alert(error.response?.data?.message || 'Failed to cancel interest. Please try again.');
    } finally {
      setInterestedLoading(false);
    }
  };

  // "Get Directions" — opens Google Maps directions from the user's current
  // location (via browser geolocation) to the property. The tab is opened
  // synchronously (before the async geolocation call resolves) so popup
  // blockers don't treat it as an unsolicited popup; we redirect it once we
  // know the origin, or immediately if geolocation is denied/unavailable
  // (Google Maps will then ask the visitor for their location itself).
  const handleGetDirections = () => {
    const lat = getPropertyLat(property);
    const lng = getPropertyLng(property);
    if (lat == null || lng == null) return;

    const destination = `${lat},${lng}`;
    const mapWindow = window.open('', '_blank', 'noopener,noreferrer');

    const navigateTo = (origin) => {
      const url = origin
        ? `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}`
        : `https://www.google.com/maps/dir/?api=1&destination=${destination}`;
      if (mapWindow) {
        mapWindow.location.href = url;
      } else {
        window.location.href = url;
      }
    };

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => navigateTo(`${position.coords.latitude},${position.coords.longitude}`),
        () => navigateTo(null),
        { timeout: 8000 }
      );
    } else {
      navigateTo(null);
    }
  };

  // Sidebar navigation handlers (only for transaction context)
  const handleSidebarNav = (tab) => {
    if (transactionId) {
      navigate(`/transaction/${transactionId}`, { state: { activeTab: tab } });
    }
  };

  const handleBackToProperties = () => {
    if (isStandalone) {
      navigate('/properties');
    } else {
      navigate(`/transaction/${transactionId}`, { state: { activeTab: 'properties' } });
    }
  };

  // Keep the active thumbnail visible in the strip — must be before any early return
  useEffect(() => {
    const strip = thumbnailStripRef.current;
    if (!strip) return;
    const active = strip.querySelector('.pd-thumb-active');
    if (active) active.scrollIntoView({ inline: 'nearest', block: 'nearest', behavior: 'smooth' });
  }, [currentPhotoIndex]);

  // Loading state
  if (isLoading || isPropertyLoading) {
    return (
      <div className="pd-container">
        <div className="pd-layout-wrapper">
          <div className={`pd-layout ${isStandalone ? 'pd-layout-standalone' : ''}`}>
            <div className="pd-main-area">
              <div className="pd-loading-state">
                <div className="pd-loading-spinner"></div>
                <h2>Loading Property Details...</h2>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const photos = getPropertyPhotos(property);
  const features = getPropertyFeatures(property);

  const handlePrevPhoto = () =>
    setCurrentPhotoIndex(i => (i - 1 + photos.length) % photos.length);
  const handleNextPhoto = () =>
    setCurrentPhotoIndex(i => (i + 1) % photos.length);

  const agentName   = getAgentName(property);
  const agentOffice = getAgentOffice(property);
  const agentEmail  = getAgentEmail(property);
  const agentPhone  = getAgentPhone(property);

  return (
    <div className="pd-container">

      <div className="pd-layout-wrapper">
      <div className={`pd-layout ${isStandalone ? 'pd-layout-standalone' : ''}`}>
        {/* Sidebar - only in transaction context */}
        {!isStandalone && (
          <div className="pd-sidebar">
            <div className="pd-sidebar-header">
              <button className="pd-back-btn-small" onClick={handleBackToProperties}>
                <span>←</span>
                <span>Back</span>
              </button>
              <div className="pd-sidebar-title">{transaction?.transactionName || 'Transaction'}</div>
              <div className="pd-sidebar-address">{transaction ? getFullAddress() : 'Property Search'}</div>
              {transaction && (
                <div className="pd-sidebar-status-row">
                  <span className="pd-sidebar-status-badge">{formatStatus(transaction.status)}</span>
                  <span className="pd-sidebar-phase">{transaction.side || ''}</span>
                </div>
              )}
            </div>

            <div className="pd-sidebar-nav">
              <div
                className="pd-sidebar-nav-item"
                onClick={() => handleSidebarNav('timeline')}
              >
                <div className="pd-sidebar-nav-label">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/><rect x="8" y="2" width="8" height="4" rx="1"/></svg>
                  <span>Timeline</span>
                </div>
              </div>
              <div
                className="pd-sidebar-nav-item"
                onClick={() => handleSidebarNav('docs')}
              >
                <div className="pd-sidebar-nav-label">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                  <span>Documents</span>
                </div>
                {documentsCount > 0 && <span className="pd-sidebar-nav-count">{documentsCount}</span>}
              </div>
              <div
                className="pd-sidebar-nav-item"
                onClick={() => handleSidebarNav('chat')}
              >
                <div className="pd-sidebar-nav-label">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                  <span>Parties</span>
                </div>
                {participantsCount > 0 && <span className="pd-sidebar-nav-count">{participantsCount}</span>}
              </div>
              <div
                className="pd-sidebar-nav-item active"
                onClick={() => handleSidebarNav('properties')}
              >
                <div className="pd-sidebar-nav-label">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
                  <span>Selected Properties</span>
                </div>
                {propertiesCount > 0 && <span className="pd-sidebar-nav-count">{propertiesCount}</span>}
              </div>
              <div
                className="pd-sidebar-nav-item"
                onClick={() => handleSidebarNav('inbox')}
              >
                <div className="pd-sidebar-nav-label">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                  <span>Inbox</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="pd-main-area">
          <div className="pd-main-scroll">
            <button className="pd-back-button" onClick={handleBackToProperties}>
              <span>←</span>
              <span>Back to Properties</span>
            </button>

            <div className="pd-grid">
              {/* Left Column */}
              <div className="pd-left-column">
                {/* Property Image */}
                <div className="pd-images-card">
                  <div
                    className="pd-main-image"
                    onTouchStart={(e) => { touchStartX.current = e.touches[0].clientX; }}
                    onTouchEnd={(e) => {
                      const delta = e.changedTouches[0].clientX - touchStartX.current;
                      if (delta > 50) handlePrevPhoto();
                      else if (delta < -50) handleNextPhoto();
                    }}
                  >
                    {photos.length > 0 ? (
                      <img src={photos[currentPhotoIndex]} alt={`Property ${currentPhotoIndex + 1}`} className="pd-main-image-img" />
                    ) : (
                      <span className="pd-main-image-placeholder">
                        <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>
                      </span>
                    )}
                    {photos.length > 1 && (
                      <>
                        <button className="pd-photo-nav pd-photo-prev" onClick={handlePrevPhoto} aria-label="Previous photo">&#8249;</button>
                        <button className="pd-photo-nav pd-photo-next" onClick={handleNextPhoto} aria-label="Next photo">&#8250;</button>
                      </>
                    )}
                    {photos.length > 0 && (
                      <div className="pd-image-counter">{currentPhotoIndex + 1} / {photos.length}</div>
                    )}
                    <button
                      className={`pd-favorite-btn${isFavorite ? ' liked' : ''}`}
                      onClick={(e) => { e.stopPropagation(); handleToggleFavorite(); }}
                      disabled={isTogglingFavorite}
                      aria-label={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
                    >
                      {isFavorite ? '♥' : '♡'}
                    </button>
                  </div>
                  {photos.length > 1 && (
                    <div className="pd-thumb-strip" ref={thumbnailStripRef}>
                      {photos.map((url, idx) => (
                        <button
                          key={idx}
                          className={`pd-thumb${idx === currentPhotoIndex ? ' pd-thumb-active' : ''}`}
                          onClick={() => setCurrentPhotoIndex(idx)}
                          aria-label={`Photo ${idx + 1}`}
                        >
                          <img src={url} alt={`Thumbnail ${idx + 1}`} className="pd-thumb-img" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Property Info Card */}
                <div className="pd-info-card">
                  <div className="pd-header-section">
                    <div className="pd-price">{getPropertyPrice(property)}</div>
                    <div className="pd-address">{getPropertyFullAddress(property)}</div>
                  </div>

                  <div className="pd-specs">
                    <div className="pd-spec-item">
                      <div className="pd-spec-value">{getPropertyBeds(property)}</div>
                      <div className="pd-spec-label">Bedrooms</div>
                    </div>
                    <div className="pd-spec-item">
                      <div className="pd-spec-value">{getPropertyBaths(property)}</div>
                      <div className="pd-spec-label">Bathrooms</div>
                    </div>
                    <div className="pd-spec-item">
                      <div className="pd-spec-value">{getPropertySqft(property)}</div>
                      <div className="pd-spec-label">Sq Ft</div>
                    </div>
                    <div className="pd-spec-item">
                      <div className="pd-spec-value">{getPropertyYearBuilt(property)}</div>
                      <div className="pd-spec-label">Year Built</div>
                    </div>
                    <div className="pd-spec-item">
                      <div className="pd-spec-value">{getPropertyDaysOnMarket(property)}</div>
                      <div className="pd-spec-label">Days</div>
                    </div>
                  </div>

                  {getPropertyDescription(property) && (
                    <div className="pd-description">
                      <div className="pd-section-title">About This Home</div>
                      <p>{getPropertyDescription(property)}</p>
                    </div>
                  )}
                </div>

                {/* Notes — private to the viewer, attached to this property. Same
                    record shown in My Notes — not a copy. */}
                <div className="pd-info-card">
                  <div className="pd-section-title pd-section-title-sm" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span>Notes</span>
                  </div>
                  <NotesPanel
                    linkType="PROPERTY"
                    linkId={mlsId}
                    linkLabel={getPropertyAddress(property)}
                  />
                </div>

                {/* Market Insight Card — canned copy, matches mobile PropertyDetailScreen */}
                <div className="pd-info-card">
                  <div className="pd-section-title pd-section-title-sm">Market Insight</div>
                  <p className="pd-market-insight-text">{getMarketInsightText(property)}</p>
                </div>

                {/* Est. Monthly Payment Card — client-side estimate, matches mobile */}
                {getEstMonthlyPayment(property) !== null && (
                  <div className="pd-info-card">
                    <div className="pd-section-title pd-section-title-sm">Est. Monthly Payment</div>
                    <div className="pd-monthly-payment-amount">${getEstMonthlyPayment(property).toLocaleString()}/mo</div>
                    <p className="pd-monthly-payment-note">
                      Including mortgage, tax, insurance{property?.hoaFee ? ', HOA' : ''}
                    </p>
                  </div>
                )}

                {/* Schools Card */}
                {getPropertySchools(property) && (
                  <div className="pd-info-card">
                    <div className="pd-section-title pd-section-title-sm">Schools</div>
                    <div className="pd-schools-list">
                      {getPropertySchools(property).elementary && (
                        <div className="pd-school-item">Elementary: {getPropertySchools(property).elementary}</div>
                      )}
                      {getPropertySchools(property).middle && (
                        <div className="pd-school-item">Middle: {getPropertySchools(property).middle}</div>
                      )}
                      {getPropertySchools(property).high && (
                        <div className="pd-school-item">High: {getPropertySchools(property).high}</div>
                      )}
                      {getPropertySchools(property).district && (
                        <div className="pd-school-district">District: {getPropertySchools(property).district}</div>
                      )}
                    </div>
                  </div>
                )}

                {/* Key Features Card */}
                {features.length > 0 && (
                  <div className="pd-info-card">
                    <div className="pd-section-title">Key Features</div>
                    <div className="pd-features-grid">
                      {features.map((feature, index) => (
                        <div className="pd-feature-item" key={index}>
                          <span className="pd-feature-icon">&#10003;</span>
                          <span>{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Right Column */}
              <div className="pd-right-column">
                {/* Action Card */}
                <div className="pd-action-card">
                  {!isAgent && (
                    <>
                      <div className="pd-interest-section">
                        <div className="pd-interest-title">
                          {isInterested ? 'You are interested in this property' : 'Interested in this property?'}
                        </div>
                        <div className="pd-interest-description">
                          {isInterested
                            ? (interestedPropertyRpaCreated
                              ? 'An RPA has been created for this property, so it can no longer be removed.'
                              : 'Your agent has been notified. You can cancel your interest below.')
                            : 'Let your agent know you\'d like to move forward with this property'}
                        </div>
                        {isInterested ? (
                          interestedPropertyRpaCreated ? null : (
                            <button
                              className="pd-interest-btn cancel-interested"
                              onClick={handleCancelInterestedClick}
                              disabled={interestedLoading}
                            >
                              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                              <span>{interestedLoading ? 'Removing...' : 'Remove'}</span>
                            </button>
                          )
                        ) : (
                          <button
                            className="pd-interest-btn"
                            onClick={handleInterested}
                            disabled={interestedLoading}
                          >
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg>
                            <span>{interestedLoading ? 'Loading...' : 'I am Interested'}</span>
                          </button>
                        )}
                      </div>

                      <div className="pd-divider"></div>
                    </>
                  )}

                  <div className="pd-action-buttons">
                    <button className="pd-action-btn-secondary">
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                      <span>Request Tour</span>
                    </button>

                    {isAgent && (
                      <button className="pd-action-btn-secondary" onClick={() => setShowShareModal(true)}>
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8"/><polyline points="16 6 12 2 8 6"/><line x1="12" y1="2" x2="12" y2="15"/></svg>
                        <span>Share Property</span>
                      </button>
                    )}
                  </div>
                </div>

                {/* Agent Card */}
                <div className="pd-agent-card">
                  <div className="pd-agent-header">
                    <div className="pd-agent-avatar">{getAgentInitials(agentName)}</div>
                    <div className="pd-agent-info">
                      <div className="pd-agent-name">{agentName || 'Listing Agent'}</div>
                      <div className="pd-agent-title">Listing Agent</div>
                      {agentOffice && (
                        <div className="pd-agent-office">{agentOffice}</div>
                      )}
                    </div>
                  </div>
                  {(agentPhone || (agentEmail && isAgentRole)) && (
                    <div className="pd-agent-contact-details">
                      {agentPhone && (
                        <div className="pd-agent-contact-row">
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 13.5 19.79 19.79 0 0 1 1.62 4.9 2 2 0 0 1 3.62 2.73h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L7.91 10a16 16 0 0 0 6 6l.93-.93a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 21.73 17z"/></svg>
                          <span>{agentPhone}</span>
                        </div>
                      )}
                      {agentEmail && isAgentRole && (
                        <div className="pd-agent-contact-row">
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                          <span>{agentEmail}</span>
                        </div>
                      )}
                    </div>
                  )}
                  <button className="pd-contact-btn">
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                    <span>Message Agent</span>
                  </button>
                </div>

                {/* Location / Map Card */}
                {getPropertyLat(property) != null && getPropertyLng(property) != null && (
                  <div className="pd-info-card pd-map-card">
                    <div className="pd-section-title pd-section-title-sm">Location</div>
                    <div className="pd-map-frame-wrapper">
                      <iframe
                        title="Property location map"
                        className="pd-map-frame"
                        src={`https://maps.google.com/maps?q=${getPropertyLat(property)},${getPropertyLng(property)}&z=15&output=embed`}
                        loading="lazy"
                        referrerPolicy="no-referrer-when-downgrade"
                      />
                    </div>
                    <button className="pd-action-btn-secondary pd-directions-btn" onClick={handleGetDirections}>
                      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="3 11 22 2 13 21 11 13 3 11"/></svg>
                      <span>Get Directions</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>

      {/* Share Property Modal */}
      <SharePropertyModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        property={property}
        getPropertyAddress={getPropertyAddress}
        getPropertyPrice={getPropertyPrice}
        getPropertyDetails={getPropertyDetails}
        getPropertyPhoto={getPropertyPhoto}
        getPropertyMls={getPropertyMls}
      />

      {/* Mark as Interested Confirmation Modal */}
      {showInterestedModal && (
        <div className="pd-modal-overlay" onClick={() => setShowInterestedModal(false)}>
          <div className="pd-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pd-confirm-title">Mark as Interested</div>
            <div className="pd-confirm-description">
              Would you like to add {getPropertyAddress(property)} to your interested properties
              {selectedTransaction && (
                <> for "<strong>{selectedTransaction.name || selectedTransaction.transactionName || 'Transaction'}</strong>"</>
              )}?
            </div>

            {buyerTransactions.length > 1 && (
              <div className="pd-confirm-select-wrapper">
                <label className="pd-confirm-select-label">Select Transaction:</label>
                <select
                  className="pd-confirm-select"
                  value={selectedTransaction?.id || selectedTransaction?.transactionId || ''}
                  onChange={(e) => {
                    const txn = buyerTransactions.find(
                      t => (t.id || t.transactionId) === e.target.value
                    );
                    setSelectedTransaction(txn);
                  }}
                >
                  {buyerTransactions.map((txn) => (
                    <option key={txn.id || txn.transactionId} value={txn.id || txn.transactionId}>
                      {txn.name || txn.transactionName || 'Transaction'}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <button
              className="pd-confirm-btn-primary"
              onClick={handleConfirmInterested}
              disabled={interestedLoading}
            >
              {interestedLoading ? 'Adding...' : 'Yes, Add Property'}
            </button>
            <button
              className="pd-confirm-btn-cancel"
              onClick={() => setShowInterestedModal(false)}
              disabled={interestedLoading}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Cancel Interested Property Confirmation Modal */}
      {showCancelInterestedConfirm && (
        <div className="pd-modal-overlay" onClick={() => !interestedLoading && setShowCancelInterestedConfirm(false)}>
          <div className="pd-confirm-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pd-confirm-title">Remove Interested Property</div>
            <div className="pd-confirm-description">
              Are you sure you want to remove {getPropertyAddress(property)} from interested properties?
            </div>

            <div className="pd-confirm-actions-row">
              <button
                className="pd-confirm-btn-primary pd-confirm-btn-compact"
                onClick={handleConfirmCancelInterested}
                disabled={interestedLoading}
              >
                {interestedLoading ? 'Removing...' : 'Yes'}
              </button>
              <button
                className="pd-confirm-btn-cancel pd-confirm-btn-compact"
                onClick={() => setShowCancelInterestedConfirm(false)}
                disabled={interestedLoading}
              >
                No
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <div className="pd-modal-overlay" onClick={() => setShowSuccessModal(false)}>
          <div className="pd-modal" onClick={(e) => e.stopPropagation()}>
            <div className="pd-modal-icon">✅</div>
            <div className="pd-modal-title">Interest Sent!</div>
            <div className="pd-modal-description">
              Your agent has been notified that you're interested in this property.
              They will review and may reach out to discuss next steps or prepare an offer.
            </div>

            <div className="pd-modal-details">
              <div className="pd-detail-row">
                <span className="pd-detail-label">Property:</span>
                <span className="pd-detail-value">{getPropertyAddress(property)}</span>
              </div>
              <div className="pd-detail-row">
                <span className="pd-detail-label">Price:</span>
                <span className="pd-detail-value">{getPropertyPrice(property)}</span>
              </div>
              <div className="pd-detail-row">
                <span className="pd-detail-label">Status:</span>
                <span className="pd-detail-value pd-detail-success">&#10003; Delivered</span>
              </div>
            </div>

            <button className="pd-modal-btn" onClick={() => setShowSuccessModal(false)}>
              Got it, Thanks!
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PropertyDetailPage;
